import { Controller } from "@hotwired/stimulus"

// Cleo at the composer. One button, "Ask Cleo", which does the fitting thing:
//
//   with words in the field  - the draft goes to our model at once and a few
//                              of Cleo's questions about it come back beneath
//   with the field empty     - a lazy Turbo Frame of three questions drawn from
//                              the member's taste opens; picking one sets it
//                              above the field as the question being answered
//
// Before a member's first page (openingUrl set), she speaks first: after a
// while at an empty box she puts one small question, the smallest door into
// what they are answering, and Ask Cleo on an empty box does the same rather
// than offering three large ones. "Quiet" keeps her from speaking first again
// for the rest of the visit.
//
// Notes also arrive unbidden, as the writer goes: once the draft has some length
// and they pause at the end of a thought - a sentence closed, or a dozen new
// words since Cleo last read it - not on every keystroke's pause. The notes
// already showing go back with the draft, so Cleo keeps the ones still
// unanswered and the notes hold still while the writing moves. Stale requests
// are abandoned; unchanged text is never sent twice.
export default class extends Controller {
  static targets = ["body", "notes", "frame", "brief", "button", "another", "reading", "tune", "tuneButton", "tuneNote"]
  static values = {
    url: String,
    // What came just before a chapter draft (the previous chapter's end), so
    // Cleo reads the chapter as part of the book and not as a post on its own.
    context: { type: String, default: "" },
    tuneUrl: String,
    // Before a first page: where her opening question comes from, what is
    // being answered (the week's question or a line from the shelf), and how
    // long an empty box sits before she speaks.
    openingUrl: { type: String, default: "" },
    about: { type: String, default: "" },
    still: { type: Number, default: 12000 },
    delay: { type: Number, default: 2000 },
    minWords: { type: Number, default: 8 },
    // New words since the last reading before Cleo reads again mid-thought.
    stride: { type: Number, default: 12 },
    // Whether Cleo reads as the member writes at all (their setting). Off,
    // she waits to be asked.
    unbidden: { type: Boolean, default: true },
    // How many of her notes show at once (their setting); the rest wait
    // behind "another".
    shows: { type: Number, default: 2 },
    // As many notes as Cleo will make on one draft before "another" rests.
    most: { type: Number, default: 9 },
    // The lines Cleo says from here, set by the page in the reader's language
    // (cleo.words in the locale); the English stands in if not.
    allSaid: { type: String, default: "That is all she has for now." },
    quiet: { type: String, default: "Cleo will wait to be asked. To turn her off for good: " },
    settings: { type: String, default: "Settings" },
    away: { type: String, default: "Away just now. Write on; the words are yours anyway." },
    kept: { type: String, default: "Kept." },
    notKept: { type: String, default: "Not kept; try again." }
  }

  connect() {
    this.lastSent = null
    // Set by "quiet" on the notes: Cleo is quiet for the rest of this post.
    this.dismissed = false
    // How many of her notes the member sees at once: their setting, and more
    // once they say "another"; kept across readings so the notes do not fold
    // back up each time she reads again.
    this.revealed = this.showsValue
    if (this.hasBodyTarget && document.activeElement === this.bodyTarget) this.sat()
  }

  disconnect() {
    clearTimeout(this.timer)
    clearTimeout(this.openingTimer)
    this.request?.abort()
  }

  // --- Speaking first, before a first page ------------------------------------

  static OPENED = "substance:cleo:opened"
  static HUSHED = "substance:cleo:hushed"

  // She waits for them to sit at the box - a click or a tap into it - and not
  // for the page to load: a newcomer looking round the people here was being
  // spoken to from below the fold, by someone they had not yet sat with.
  sat() {
    if (!this.opened && !this.request) this.waitToOpen()
  }

  // Up and gone from the box before she spoke: she keeps her question.
  rose() {
    clearTimeout(this.openingTimer)
  }

  waitToOpen() {
    if (!this.openingUrlValue || this.words() > 0 || sessionStorage.getItem(this.constructor.HUSHED)) return
    // Back at the box this visit: she has already asked, and asks again at once.
    const still = sessionStorage.getItem(this.constructor.OPENED) ? 1500 : this.stillValue
    clearTimeout(this.openingTimer)
    this.openingTimer = setTimeout(() => this.open(), still)
  }

  async open({ another = false } = {}) {
    if (!this.openingUrlValue || this.request || (!another && this.words() > 0)) return
    const request = (this.request = new AbortController())
    this.notesTarget.dataset.state = "thinking"
    try {
      const params = new URLSearchParams({ about: this.aboutValue })
      if (another) params.set("asked", this.asked().join("\n"))
      const response = await fetch(this.openingUrlValue, {
        method: "POST",
        headers: { "Accept": "text/html", "X-CSRF-Token": this.csrfToken, "X-Requested-With": "XMLHttpRequest" },
        body: params,
        signal: request.signal
      })
      if (!response.ok || response.status === 204) return
      this.show(await response.text())
      this.opened = true
      const question = this.notesTarget.querySelector(".cleo-note")?.textContent.trim()
      if (question) sessionStorage.setItem(this.constructor.OPENED, JSON.stringify([...this.asked(), question].slice(-6)))
    } catch (error) {
      if (error.name !== "AbortError") console.warn("[cleo]", error)
    } finally {
      if (this.request === request) this.request = null
      delete this.notesTarget.dataset.state
    }
  }

  anotherOpening() {
    this.open({ another: true })
  }

  // The questions she has opened with this visit, so "another" is another.
  asked() {
    try { return JSON.parse(sessionStorage.getItem(this.constructor.OPENED) || "[]") } catch { return [] }
  }

  // --- Ask Cleo ---------------------------------------------------------------

  consult() {
    if (this.words() > 0) {
      this.hideIdeas()
      this.ask({ asked: true })
    } else if (this.openingUrlValue) {
      clearTimeout(this.openingTimer)
      this.open({ another: this.opened })
    } else if (this.hasFrameTarget) {
      // Unhiding the frame is what lets its lazy load begin.
      this.frameTarget.hidden = !this.frameTarget.hidden
      this.pressed(!this.frameTarget.hidden)
    }
  }

  adopt({ params: { idea } }) {
    this.briefTarget.textContent = idea
    this.briefTarget.hidden = false
    this.hideIdeas()
    this.bodyTarget.focus()
  }

  hideIdeas() {
    if (this.hasFrameTarget) this.frameTarget.hidden = true
    this.pressed(false)
  }

  pressed(open) {
    if (this.hasButtonTarget) this.buttonTarget.setAttribute("aria-pressed", String(open))
  }

  // --- Notes -----------------------------------------------------------------

  changed() {
    clearTimeout(this.timer)
    clearTimeout(this.openingTimer)
    // Three questions for an empty box are not for a box with words in it.
    if (this.words() > 0) this.hideIdeas()
    if (this.aside) this.clear()
    if (!this.unbiddenValue || this.dismissed) return
    this.timer = setTimeout(() => this.settled(), this.delayValue)
  }

  // "Another": the next of her questions, kept back until asked for - and
  // once they are all out, a fresh reading for more, about the parts of the
  // draft she has not asked about. She has her say and then rests.
  another() {
    const hidden = this.notesTarget.querySelector(".cleo-note[hidden]")
    if (hidden) {
      hidden.hidden = false
      this.revealed = this.notesTarget.querySelectorAll(".cleo-note:not([hidden])").length
      this.rest()
      return
    }
    this.more()
  }

  async more() {
    if (this.request || !this.words()) return
    const text = this.bodyTarget.value.trim()
    const request = (this.request = new AbortController())
    this.notesTarget.dataset.state = "thinking"
    if (this.hasAnotherTarget) this.anotherTarget.disabled = true

    try {
      const params = new URLSearchParams({ body: text, more: "1", previous: this.showing() })
      if (this.contextValue) params.set("context", this.contextValue)
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "Accept": "text/html", "X-CSRF-Token": this.csrfToken, "X-Requested-With": "XMLHttpRequest" },
        body: params,
        signal: request.signal
      })

      const fresh = response.ok && response.status !== 204 ? this.notesIn(await response.text()) : []
      if (fresh.length === 0) {
        this.said(this.allSaidValue)
        return
      }

      const tools = this.notesTarget.querySelector(".cleo-tools")
      fresh.forEach((note, i) => {
        note.hidden = i > 0
        tools ? tools.before(note) : this.notesTarget.querySelector(".cleo-said-notes")?.append(note)
      })
      this.revealed = this.notesTarget.querySelectorAll(".cleo-note:not([hidden])").length
      this.lastSent = text
      this.rest()
    } catch (error) {
      if (error.name !== "AbortError") console.warn("[cleo]", error)
    } finally {
      if (this.request === request) this.request = null
      delete this.notesTarget.dataset.state
      if (this.hasAnotherTarget) this.anotherTarget.disabled = false
    }
  }

  // The notes in a page of Cleo's, as elements of their own.
  notesIn(html) {
    const page = document.createElement("template")
    page.innerHTML = html
    return [...page.content.querySelectorAll(".cleo-note")]
  }

  // "Another" rests once Cleo has made as many notes on this draft as she will.
  rest() {
    if (!this.hasAnotherTarget) return
    this.anotherTarget.hidden = this.notesTarget.querySelectorAll(".cleo-note").length >= this.mostValue
  }

  // A faint line of Cleo's beneath the notes, in place of any earlier one.
  said(text) {
    this.notesTarget.querySelector(".cleo-aside")?.remove()
    const line = document.createElement("p")
    line.className = "cleo-aside"
    line.textContent = text
    this.notesTarget.append(line)
    if (this.hasAnotherTarget) this.anotherTarget.hidden = true
  }

  // "Quiet". The notes go, and Cleo does not come back unbidden for the rest
  // of this post; Ask Cleo still works. One faint line says so, and where to
  // turn her off for good; it goes at the next keystroke.
  dismiss() {
    this.dismissed = true
    clearTimeout(this.openingTimer)
    if (this.openingUrlValue) sessionStorage.setItem(this.constructor.HUSHED, "1")
    this.request?.abort()
    this.closeTune()
    this.notesTarget.innerHTML = ""
    const line = document.createElement("p")
    line.className = "cleo-aside"
    line.append(this.quietValue)
    const link = document.createElement("a")
    link.href = "/settings"
    link.textContent = this.settingsValue
    line.append(link, ".")
    this.notesTarget.append(line)
    this.aside = true
    this.bodyTarget.focus()
  }

  // The writer has paused. Cleo reads again only at the end of a thought: the
  // draft closes on a full stop or a question, or has grown by a stride of
  // words since she last read it. A pause mid-sentence is left alone.
  settled() {
    const text = this.bodyTarget.value.trim()
    if (this.words() < this.minWordsValue) {
      // Her opening question stays while it is being answered.
      if (!this.opened) this.clear()
      return
    }
    if (text === this.lastSent) return

    const closed = /[.?!…]["”’)]?$/.test(text)
    const grown = Math.abs(this.words() - this.countWords(this.lastSent ?? "")) >= this.strideValue
    if (closed || grown || this.lastSent === null) this.ask()
  }

  async ask({ asked = false } = {}) {
    const text = this.bodyTarget.value.trim()

    if (!asked && this.words() < this.minWordsValue) {
      this.clear()
      return
    }
    if (!asked && text === this.lastSent) return

    this.lastSent = text
    this.request?.abort()
    const request = (this.request = new AbortController())
    this.reading()
    if (asked && this.hasButtonTarget) this.buttonTarget.dataset.state = "thinking"

    try {
      const params = new URLSearchParams({ body: text })
      if (asked) params.set("asked", "1")
      if (this.contextValue) params.set("context", this.contextValue)
      const previous = this.showing()
      if (previous) params.set("previous", previous)

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "text/html",
          "X-CSRF-Token": this.csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: params,
        signal: request.signal
      })

      if (response.ok && response.status !== 204) {
        this.show(await response.text(), { asked })
      } else if (response.status === 204) {
        this.clear()
      } else if (asked) {
        this.away()
      }
      // Otherwise the notes we already show stand; Cleo is quiet.
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn("[cleo]", error)
        if (asked) this.away()
      }
    } finally {
      if (this.request === request) this.request = null
      delete this.notesTarget.dataset.state
      if (this.hasButtonTarget) delete this.buttonTarget.dataset.state
      // Nothing came, and she was only reading: the dots go.
      if (this.notesTarget.querySelector(".cleo-reading") && !this.notesTarget.querySelector(".cleo-note")) this.clear()
    }
  }

  // Cleo is reading. With notes already up they dim and the dots breathe in
  // their tools; with none, her name and the three dots take the place the
  // notes will.
  reading() {
    this.notesTarget.dataset.state = "thinking"
    if (this.notesTarget.querySelector(".cleo-note") || !this.hasReadingTarget) return
    this.notesTarget.replaceChildren(this.readingTarget.content.cloneNode(true))
    this.notesTarget.hidden = false
    this.aside = false
  }

  // Asked outright, everything she has to say shows; unbidden, as many notes
  // as the member has set.
  show(html, { asked = false } = {}) {
    this.notesTarget.innerHTML = html
    this.notesTarget.hidden = false
    this.aside = false
    this.opened = false
    this.reveal(asked ? Infinity : this.revealed)
  }

  // As many notes as the member has asked to see: their setting, or more
  // once they said "another" on this post.
  reveal(count = this.revealed) {
    const notes = [...this.notesTarget.querySelectorAll(".cleo-note")]
    notes.forEach((note, i) => (note.hidden = i >= count))
    this.rest()
  }

  clear() {
    this.notesTarget.hidden = true
    this.notesTarget.innerHTML = ""
    this.lastSent = null
    this.opened = false
    this.aside = false
    this.closeTune()
  }

  // --- Tune ------------------------------------------------------------------
  // The small panel behind "Tune": as you write, how many at once, questions
  // or remarks too. Each change is saved as the member's setting and takes
  // effect here at once.

  tune() {
    if (!this.hasTuneTarget) return
    const open = this.tuneTarget.hidden
    this.tuneTarget.hidden = !open
    if (this.hasTuneButtonTarget) this.tuneButtonTarget.setAttribute("aria-expanded", String(open))
    if (open) this.tuneTarget.querySelector("input:checked, input")?.focus()
  }

  closeTune() {
    if (this.hasTuneTarget) this.tuneTarget.hidden = true
    if (this.hasTuneButtonTarget) this.tuneButtonTarget.setAttribute("aria-expanded", "false")
  }

  async retune({ target }) {
    const value = target.type === "checkbox" ? target.checked : target.value
    const setting = target.name

    if (setting === "cleo_as_you_write") {
      this.unbiddenValue = target.checked
      if (!this.unbiddenValue) clearTimeout(this.timer)
    } else if (setting === "cleo_notes") {
      this.showsValue = Number(value)
      this.revealed = this.showsValue
      this.reveal()
    }

    this.tuneSaid("")
    try {
      const response = await fetch(this.tuneUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken, "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ cleo: { [setting]: value } })
      })
      if (!response.ok) throw new Error(`Cleo tune ${response.status}`)
      this.tuneSaid(this.keptValue)
      // A new manner reads the draft afresh, if there is one to read.
      if (setting === "cleo_manner" && this.words() >= this.minWordsValue && !this.dismissed) {
        this.lastSent = null
        this.ask()
      }
    } catch (error) {
      console.warn("[cleo]", error)
      this.tuneSaid(this.notKeptValue)
    }
  }

  tuneSaid(text) {
    if (this.hasTuneNoteTarget) this.tuneNoteTarget.textContent = text
  }

  // Asked, and the model could not be reached: say so, rather than nothing.
  away() {
    this.notesTarget.innerHTML = ""
    const said = document.createElement("div")
    said.className = "cleo-said"
    const voice = document.createElement("span")
    voice.className = "cleo-voice"
    voice.textContent = "Cleo"
    const line = document.createElement("p")
    line.className = "cleo-note"
    line.textContent = this.awayValue
    said.append(voice, line)
    this.notesTarget.append(said)
    this.notesTarget.hidden = false
    this.lastSent = null
  }

  // The questions on show, one per line, as they read - the ones kept behind
  // "another" included, so Cleo does not ask them again as new. A remark is
  // not among them: it read the draft as it was, and is made afresh or not
  // at all. The member's quoted words are set in <q>, whose marks are CSS;
  // they are put back so the model sees the note as it wrote it.
  showing() {
    if (this.notesTarget.hidden) return ""
    return [...this.notesTarget.querySelectorAll(".cleo-note")]
      .map((note) => [...note.childNodes].map((n) => (n.nodeName === "Q" ? `"${n.textContent}"` : n.textContent)).join("").trim())
      .filter((note) => note.endsWith("?"))
      .join("\n")
  }

  words() {
    return this.countWords(this.bodyTarget.value)
  }

  countWords(text) {
    return text.trim().split(/\s+/).filter(Boolean).length
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content ?? ""
  }
}
